import { FormControl, FormControlProps, StackProps } from '@mui/material';
import { Editor, EditorContentProps } from '@provision/rich-text-editor';
import { lazy, MutableRefObject, Suspense, useCallback } from 'react';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { getRawTextFromHTML, hasImgTags } from 'src/utils/text-parsing';

const RichTextEditor = lazy(() => import('src/components/RichTextEditor/rich-text-editor'));

export type FormRichTextProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name'
> & {
  label?: string;
  required?: boolean | string;
  disabled?: boolean;
  buttonText?: string;
  value?: string;
  placeholder?: string;
  withSubmitButton?: boolean;
  withToolBarOtherOptions?: boolean;
  editorRef?: MutableRefObject<Editor | null>;
} & { editorContainerProps?: Partial<StackProps> } & {
  editorContentProps?: Omit<Partial<EditorContentProps>, 'editor' | 'ref'>;
} & CommonFormFieldProps<'rich-text', string>;

export const FormRichText = (props: FormRichTextProps) => {
  const {
    name,
    required,
    children,
    editorContainerProps,
    editorContentProps,
    buttonText,
    disabled,
    placeholder,
    withSubmitButton = true,
    withToolBarOtherOptions = true,
    defaultValue,
    editorRef,
    sx,
    ...rest
  } = props;
  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;

  const onSubmit = useCallback(
    (field: ControllerRenderProps) => (editorText: string) => {
      if (getRawTextFromHTML(editorText) || hasImgTags(editorText)) {
        field.onChange(editorText);
      }
    },
    [],
  );

  return (
    <FormControl variant="standard" {...rest} error={!!errorMsg}>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field }) => (
          <Suspense>
            <RichTextEditor
              content={field.value as string}
              editorRef={editorRef}
              disabled={disabled}
              error={errorMsg?.toString()}
              editorOptions={{
                onUpdate: ({ editor }: { editor: Editor }) => {
                  const fieldContent = editor.getHTML().trim();
                  const actualText = getRawTextFromHTML(fieldContent);
                  const nextValue = actualText || hasImgTags(fieldContent) ? fieldContent : '';
                  if (field.value !== nextValue) {
                    field.onChange(nextValue);
                  }
                },
              }}
              onBlur={(_, editor) => {
                field.onBlur();

                const fieldContent = editor?.getHTML().trim() || '';
                const actualTextWithoutMarkup = getRawTextFromHTML(fieldContent);
                if (actualTextWithoutMarkup) {
                  field.onChange(fieldContent);
                }
              }}
              onSubmit={onSubmit(field)}
              editorContentProps={{
                ...editorContentProps,

                innerRef: field.ref,
              }}
              buttonText={buttonText}
              withSubmitButton={withSubmitButton}
              withToolBarOtherOptions={withToolBarOtherOptions}
              placeholderOptions={{ placeholder }}
              sx={{
                // TODO: Refactor `chat-utils` package to provide a better way
                // to style rich text editor.
                '& .portal-editor': {
                  maxHeight: '15rem',
                },
                ...sx,
              }}
            />
          </Suspense>
        )}
      />
    </FormControl>
  );
};
